import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const Testimonials = ({ isAdmin = false, onEditClick }) => {
  const [testimonialsData, setTestimonialsData] = useState({
    heading: "Patient Testimonials",
    subheading: "Hear what our patients say about their experiences at Warm Wellness Center",
    testimonials: [],
    gradientColors: ['#0760ad', '#098a48', '#118a94', '#339166', '#339166']
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonialsData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/home/testimonials/`);
        if (response.ok) {
          const data = await response.json();
          setTestimonialsData({
            heading: data.heading || "Patient Testimonials",
            subheading: data.subheading || "Hear what our patients say about their experiences...",
            testimonials: data.testimonials || [],
            gradientColors: data.gradientColors || ['#0760ad', '#098a48', '#118a94', '#339166', '#339166']
          });
        }
      } catch (error) {
        console.error('Error fetching testimonials data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestimonialsData();
  }, []);

  const StarRating = ({ rating }) => {
    return (
      <div className="flex mb-4">
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            className={`w-5 h-5 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <section className="py-20 relative overflow-hidden bg-gradient-to-r from-blue-600 to-green-500">
        <div className="container mx-auto px-4 flex justify-center items-center h-64">
          <span className="loading loading-spinner loading-lg text-white"></span>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="py-20 relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${testimonialsData.gradientColors.join(', ')})`
      }}
    >
      {isAdmin && (
        <button 
          onClick={onEditClick}
          className="absolute top-4 right-4 btn btn-sm btn-primary z-20"
        >
          Edit Section
        </button>
      )}

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-10">
        <div className="absolute top-20 left-10 w-40 h-40 rounded-full bg-white mix-blend-overlay"></div>
        <div className="absolute bottom-10 right-10 w-60 h-60 rounded-full bg-white mix-blend-overlay"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">{testimonialsData.heading}</h2>
          <p className="text-xl text-white opacity-90 max-w-2xl mx-auto">
            {testimonialsData.subheading}
          </p>
        </div>
        
        {testimonialsData.testimonials.length > 0 ? (
          <>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              navigation={{
                nextEl: '.testimonial-next',
                prevEl: '.testimonial-prev'
              }}
              pagination={{ 
                clickable: true,
                dynamicBullets: true,
                renderBullet: (index, className) => {
                  return `<span class="${className}" style="background-color: white; width: 12px; height: 12px;"></span>`;
                }
              }}
              autoplay={{ 
                delay: 6000,
                disableOnInteraction: false
              }}
              breakpoints={{
                768: {
                  slidesPerView: 2
                },
                1024: {
                  slidesPerView: 3
                }
              }}
              className="pb-16"
            >
              {testimonialsData.testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <div className="bg-white p-8 rounded-2xl shadow-xl h-full transform transition-all hover:scale-105 hover:shadow-2xl duration-300">
                    <div className="flex items-start mb-6">
                      {testimonial.image ? (
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/48?text=Avatar';
                          }}
                        />
                      ) : (
                        <svg 
                          className="w-12 h-12 text-gray-200 mr-4" 
                          fill="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                        </svg>
                      )}
                      <div>
                        <p className="text-lg text-gray-700">{testimonial.quote}</p>
                        <StarRating rating={testimonial.rating} />
                      </div>
                    </div>
                    <div className="border-t border-gray-100 pt-4">
                      <p className="font-bold text-gray-800">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            <div className="flex justify-center mt-8 space-x-4">
              <button className="testimonial-prev bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button className="testimonial-next bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-12 bg-white bg-opacity-20 rounded-xl">
            <p className="text-white text-xl">No testimonials available yet</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;